import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
  useAivyContext,
  useNotificationContext,
  useUserContext
} from '../../context'
import { uuidv4 } from '../../helpers'

import EmailClientIllustration from '../../assets/images/emailclient.png'
import {
  MAIL_TEMPLATE_KINDS,
  PLACEHOLDERS,
  PLACEHOLDERS_BY_KIND
} from '../../constants/mail-template-placeholders'
import trackEvents from '../../constants/track-events'
import { query } from '../../graphql'
import { trackEvent } from '../../helpers/analytics'
import { collectMailTemplateIssues } from '../../helpers/mail-template-placeholders'
import InputFormik from '../input-formik'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'
import Tiptap from '../tiptap/tiptap'
import MailPreviewDialog from './mail-preview-dialog'
import MailTemplateIssues from './mail-template-issues'
import MailTemplatePreview from './mail-template-preview'

const I18N = 'mail_template.edit_mail_template.'

// Im Betreff ersetzt inviteTalent nur Firma und Stelle
const SUBJECT_TOKENS = PLACEHOLDERS_BY_KIND[MAIL_TEMPLATE_KINDS.SUBJECT].map(
  (name) => PLACEHOLDERS[name].token
)

const EditMailTemplate = ({
  open,
  setOpen,
  mailTemplate,
  mailTemplateMacros,
  handleSubmit,
  preview,
  recipient,
  onEdit
}) => {
  const { t } = useTranslation()
  const { cognitoUser } = useUserContext()
  const { system } = useAivyContext()
  const { success, error } = useNotificationContext()

  const contentLabelId = useId()
  const issuesId = useId()

  const [isLoadingSendPreview, setIsLoadingSendPreview] = useState(false)
  const [sentTo, setSentTo] = useState(null)
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  const sendPreviewTimeoutRef = useRef(null)

  useEffect(() => () => clearTimeout(sendPreviewTimeoutRef.current), [])

  // Die Liste kommt bei jedem Render neu, der Schlüssel hält useMemo stabil
  const allowedKey = mailTemplateMacros.map(({ macro }) => macro).join('|')

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      subject: mailTemplate.subject,
      data: JSON.parse(mailTemplate.data)
    },
    validationSchema: Yup.object({
      subject: Yup.string().required(
        t('mail_template.edit_mail_template.subject_validation')
      )
    }),
    onSubmit: ({ subject, data }, { setSubmitting }) => {
      // Speichern ist bei Problemen gesperrt, Enter im Betreff landet trotzdem hier
      if (issues.length) {
        setSubmitting(false)
        return
      }

      setSubmitting(true)

      const result = {
        id: mailTemplate.id,
        createdAt: mailTemplate.createdAt,
        subject,
        data: JSON.stringify(data),
        updatedAt: new Date().toISOString()
      }

      // create a new template
      if (!result.id) {
        result.id = uuidv4()
        result.createdAt = new Date().toISOString()
      }

      const onSuccess = () => {
        setSubmitting(false)
        success(
          t('mail_template.edit_mail_template.update_mail_templates_success')
        )
      }

      handleSubmit({ result, onSuccess, onError: () => setSubmitting(false) })
    }
  })

  // Unbekannte, für den Typ nicht erlaubte oder durch Formatierung zerbrochene
  // Platzhalter landen sonst roh in der Mail. Prüft schon beim Öffnen, damit
  // auch alte Vorlagen ihre Probleme zeigen.
  const issues = useMemo(() => {
    const allowed = allowedKey ? allowedKey.split('|') : []
    return collectMailTemplateIssues({
      doc: formik.values.data,
      subject: formik.values.subject,
      allowed,
      subjectAllowed: allowed.filter((token) => SUBJECT_TOKENS.includes(token))
    })
  }, [formik.values.data, formik.values.subject, allowedKey])

  const closeModal = () => {
    setConfirmDiscardOpen(false)
    setOpen(false)
    formik.resetForm()
  }

  // Escape, Klick daneben, X und Abbrechen fragen bei Änderungen nach
  const requestClose = () => {
    if (formik.dirty) setConfirmDiscardOpen(true)
    else closeModal()
  }

  // Nur zur Anzeige. Das Backend schickt immer an die bestätigte
  // Login-Adresse aus Cognito, egal was hier steht.
  const loginEmail = cognitoUser?.signInUserSession?.idToken?.payload?.email

  // inviteTalent baut die Test-Mail mit derselben Makro-Ersetzung wie die echte
  // Mail, mit Beispielwerten statt eines Talents
  const handleSendTestMail = () => {
    trackEvent(trackEvents.SEND_TEST_EMAIL)
    setIsLoadingSendPreview(true)

    const variables = {
      email_template: mailTemplate.emailTemplate,
      subject: formik.values.subject,
      data: JSON.stringify(formik.values.data),
      career_id: mailTemplate.careerId,
      language: mailTemplate.language
    }

    query({ query: 'sendTestMail', variables })
      .then((response) => {
        const { recipient: sentRecipient } = JSON.parse(response || '{}')

        setSentTo(sentRecipient || loginEmail)
        sendPreviewTimeoutRef.current = setTimeout(() => {
          setIsLoadingSendPreview(false)
          setSentTo(null)
        }, 1500)
      })
      .catch((err) => {
        setIsLoadingSendPreview(false)
        // Fehlende Rechte meldet query() schon selbst
        if (err.message !== 'NOT_AUTHORIZED') error(t(I18N + 'send_test.error'))
      })
  }

  const testMail = (
    <div className='flex flex-col items-start'>
      <Button.Text
        text={t('mail_template.edit_mail_template.send_test_action')}
        isLoading={isLoadingSendPreview}
        onClick={handleSendTestMail}
        disabled={issues.some(({ code }) => code === 'empty_body')}
      />
      <span
        role='status'
        className={
          sentTo
            ? 'mt-1 block text-sm text-green-600'
            : 'mt-1 block text-xs text-gray-500'
        }
      >
        {sentTo
          ? t(I18N + 'send_test.success', { email: sentTo })
          : loginEmail &&
            t(I18N + 'send_test.recipient', { email: loginEmail })}
      </span>
    </div>
  )

  if (preview) {
    return (
      <MailPreviewDialog
        open={open}
        onClose={closeModal}
        mailTemplate={mailTemplate}
        data={formik.values.data}
        mailTemplateMacros={mailTemplateMacros}
        recipient={recipient}
        onEdit={onEdit}
        footerStart={testMail}
      />
    )
  }

  return (
    <Modal open={open} setOpen={requestClose} size={'5xl'}>
      <h1 className='text-2xl font-bold text-gray-900'>
        {t('mail_template.edit_mail_template.title')}
      </h1>
      <div className='mt-4 grid grid-cols-1 grid-rows-2 gap-x-8 gap-y-4 lg:grid-cols-2 lg:grid-rows-1'>
        <div className='w-full'>
          <h2 className='mb-1 font-semibold text-gray-900'>
            {t('mail_template.edit_mail_template.content_label')}
          </h2>
          <div>
            <InputFormik
              {...{
                id: 'subject',
                type: 'text',
                formik,
                label: t(I18N + 'subject_label'),
                description: t(I18N + 'subject_description')
              }}
            />
            <div className='mt-4'>
              <p
                id={contentLabelId}
                className='mb-1 block text-sm font-medium text-gray-700'
              >
                {t(I18N + 'body_label')}
              </p>
              <Tiptap
                initialContent={formik.initialValues.data}
                placeholders={mailTemplateMacros}
                labelledBy={contentLabelId}
                describedBy={issuesId}
                onUpdate={({ editor }) => {
                  formik.setFieldValue('data', editor.getJSON())
                }}
              />
              <MailTemplateIssues id={issuesId} issues={issues} />
            </div>
          </div>
        </div>
        <div className='w-full'>
          <h2 className='mb-1 font-semibold text-gray-900'>
            {t('mail_template.edit_mail_template.preview_label')}
          </h2>
          <div>
            <div className='flex h-10 items-center'>
              <span className='text-base font-bold text-gray-900'>
                {formik.values.subject}
              </span>
            </div>
            <img
              src={EmailClientIllustration}
              alt=''
              className='mt-4 h-auto w-full overflow-hidden rounded-t-xl'
            />
            <div className='max-h-[28rem] min-h-[14rem] overflow-y-auto rounded-b-xl border border-gray-200'>
              <MailTemplatePreview
                system={system}
                imageUrl={mailTemplate.imageUrl}
                language={mailTemplate.language}
                useInvitationBlock={mailTemplate.useInvitationBlock}
                data={formik.values.data}
                mailTemplateMacros={mailTemplateMacros}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='mt-8 flex items-center justify-between gap-x-2'>
        {testMail}
        <div className='flex items-center gap-x-2'>
          <Button.SecondaryLG
            text={t('mail_template.edit_mail_template.cancel_button_text')}
            onClick={requestClose}
          />
          <Button.PrimaryLG
            text={t('mail_template.edit_mail_template.save_button_text')}
            onClick={formik.handleSubmit}
            disabled={!formik.dirty || issues.length > 0}
            isLoading={formik.isSubmitting}
          />
        </div>
      </div>
      <Modal
        open={confirmDiscardOpen}
        setOpen={() => setConfirmDiscardOpen(false)}
        size='md'
        zIndex='z-50'
        hideCloseButton
      >
        <h2 className='text-lg font-semibold text-gray-900'>
          {t(I18N + 'discard.title')}
        </h2>
        <p className='mt-2 text-sm text-gray-600'>{t(I18N + 'discard.text')}</p>
        <div className='mt-6 flex justify-end gap-x-2'>
          <Button.SecondaryLG
            text={t(I18N + 'discard.cancel')}
            onClick={() => setConfirmDiscardOpen(false)}
          />
          <Button.WarningLG
            text={t(I18N + 'discard.confirm')}
            onClick={closeModal}
          />
        </div>
      </Modal>
    </Modal>
  )
}

EditMailTemplate.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  mailTemplate: PropTypes.object.isRequired,
  mailTemplateMacros: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func,
  preview: PropTypes.bool,
  // Nur für die Vorschau. Talent, soweit bekannt, und der Weg in den Editor.
  recipient: PropTypes.object,
  onEdit: PropTypes.func
}

EditMailTemplate.defaultProps = {
  handleSubmit: () => undefined,
  preview: false,
  recipient: undefined,
  onEdit: undefined
}

export default EditMailTemplate
